import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { ArrowRight, ArrowLeft, TrendingUp, SkipForward } from 'lucide-react';
import { Logo } from '../../Logo';

export const BusinessGrowthSubScreen = () => {
  const { data, updateExpansionDetails, nextStep, prevStep, skipSubScreen } = useQuickscan();
  
  const [growthData, setGrowthData] = useState({
    growthPercentage: data.expansionDetails.growth?.growthPercentage || 25,
    timeframe: data.expansionDetails.growth?.timeframe || '1-2 jaar',
    growthType: data.expansionDetails.growth?.growthType || '',
    additionalEmployees: data.expansionDetails.growth?.additionalEmployees || 10,
    growthTypes: data.expansionDetails.growth?.growthTypes || []
  });

  const handleNext = () => {
    updateExpansionDetails?.('growth', growthData);
    nextStep();
  };

  const handleSkip = () => {
    console.log('Skipping business growth details');
    skipSubScreen?.();
  };

  const timeframes = ['6 maanden', '1-2 jaar', '2-3 jaar', '3-5 jaar', '5+ jaar'];
  const growthTypes = [
    'Meer personeel',
    'Nieuwe productlijnen',
    'Uitbreiding capaciteit',
    'Nieuwe markten',
    'Acquisities',
    'Internationale expansie'
  ];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb with logo */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-gray-600">
          <span>Expansieplannen</span> <span className="mx-2">{'>'}‍</span> <span className="text-energy-blue font-medium">Bedrijfsgroei</span>
        </div>
        <Logo className="h-6 object-contain" />
      </div>

      <div className="text-center mb-8">
        <TrendingUp className="w-12 h-12 text-energy-blue mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Details bedrijfsgroei
        </h2>
        <p className="text-gray-600">
          Hoe ziet de groei van jouw bedrijf eruit?
        </p>
      </div>

      <div className="space-y-6 mb-8">
        {/* Growth Percentage */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Groeipercentage <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={growthData.growthPercentage}
            onChange={(e) => setGrowthData(prev => ({ ...prev, growthPercentage: parseInt(e.target.value) || 0 }))}
            min="5"
            max="100"
            className="w-full rounded-md border border-gray-200 px-3 py-2 shadow-sm focus:border-energy-blue focus:outline-none focus:ring-energy-blue/50"
          />
          <p className="text-xs text-gray-500 mt-1">
            Huidige waarde: {growthData.growthPercentage}%
          </p>
        </div>

        {/* Timeframe */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Tijdsbestek <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            {timeframes.map((timeframe) => (
              <button
                key={timeframe}
                onClick={() => setGrowthData(prev => ({ ...prev, timeframe }))}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  growthData.timeframe === timeframe
                    ? 'border-energy-blue bg-energy-blue/10 text-energy-blue font-medium' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                {timeframe}
              </button>
            ))}
          </div>
        </div>

        {/* Growth Type */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Type groei <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {growthTypes.map((growthType) => (
              <button
                key={growthType}
                onClick={() => setGrowthData(prev => ({ ...prev, growthType }))}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  growthData.growthType === growthType
                    ? 'border-energy-blue bg-energy-blue/10 text-energy-blue font-medium' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                {growthType}
              </button>
            ))}
          </div>
        </div>

        {/* Additional Employees */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Extra medewerkers
          </label>
          <input
            type="number"
            value={growthData.additionalEmployees}
            onChange={(e) => setGrowthData(prev => ({ ...prev, additionalEmployees: parseInt(e.target.value) || 0 }))}
            min="0"
            max="1000"
            className="w-full rounded-md border border-gray-200 px-3 py-2 shadow-sm focus:border-energy-blue focus:outline-none focus:ring-energy-blue/50"
          />
          <p className="text-xs text-gray-500 mt-1">
            Aantal extra medewerkers verwacht
          </p>
        </div>
      </div>
      
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug
        </Button>

        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleSkip}
            className="border-gray-400 text-gray-600 hover:bg-gray-50"
          >
            <SkipForward className="w-4 h-4 mr-1" /> Overslaan
          </Button>
          
          <Button 
            onClick={handleNext}
            disabled={!growthData.growthType}
            className="bg-[#014336] text-white hover:bg-[#014336]/90"
          >
            Volgende <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};
